import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/help/tool-call-patterns")({
	component: ToolCallPatterns,
});

const patterns = [
	{
		title: "One-shot generation",
		code: `agent.call_tool("agentframer", {
  action: "generate_image",
  prompt: "Editorial product shot on brushed steel"
})`,
		description:
			"Use for simple media requests where the agent can submit a prompt and wait for a stored output URL in a single step.",
	},
	{
		title: "Model override",
		code: `agent.call_tool("agentframer", {
  action: "generate_video",
  model: "klingai-video-3.0-pro",
  prompt: "10 second camera orbit around a sneaker"
})`,
		description:
			"Override the default model only when the prompt needs a specific provider capability, format, or quality profile.",
	},
	{
		title: "Recovering previous output",
		code: `agent.call_tool("agentframer", {
  action: "list_recent_generations"
})

agent.call_tool("agentframer", {
  action: "get_generation",
  generationId: "<id>"
})`,
		description:
			"Ask for recent generations before retrying work. This prevents duplicate jobs when the assistant reconnects or loses local state.",
	},
];

export function ToolCallPatterns() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>MCP Reference</p>
				<h1
					className="text-4xl font-semibold tracking-tight"
					style={{
						color: "var(--km-on-surface)",
						fontFamily: "'Geist', -apple-system, sans-serif",
					}}
				>
					Tool call patterns
				</h1>
				<p className="text-sm" style={{ color: "var(--km-on-surface-muted)" }}>
					Practical MCP request shapes for generation, recovery, and model
					selection.
				</p>
			</div>

			<div
				className="space-y-5 text-sm leading-relaxed"
				style={{ color: "var(--km-on-surface-muted)" }}
			>
				<p>
					AgentFramer works best when the assistant chooses the smallest tool
					sequence that answers the user request. Prefer direct generation calls
					for new work, and use history lookups when the task may already be
					complete.
				</p>
				<p>
					Avoid multi-step chains unless the result of one tool call changes the
					next decision. That keeps latency low and reduces duplicate media
					jobs.
				</p>
			</div>

			<div className="space-y-8">
				{patterns.map((pattern) => (
					<section key={pattern.title} className="space-y-3">
						<h2
							className="text-base font-semibold"
							style={{
								color: "var(--km-on-surface)",
								fontFamily: "'Geist', -apple-system, sans-serif",
							}}
						>
							{pattern.title}
						</h2>
						<pre
							className="overflow-x-auto p-4 text-xs"
							style={{
								backgroundColor: "#0a0a0a",
								border: "1px solid rgba(255,255,255,0.08)",
								borderLeft: "3px solid #6B3FF2",
								color: "#f5f5f5",
								fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
							}}
						>
							<code>{pattern.code}</code>
						</pre>
						<p style={{ color: "var(--km-on-surface-muted)" }}>
							{pattern.description}
						</p>
					</section>
				))}
			</div>

			<div
				className="space-y-3 text-sm leading-relaxed"
				style={{ color: "var(--km-on-surface-muted)" }}
			>
				<p>
					When an assistant needs a full list of available actions or parameter
					names, use the MCP tools reference. Use this page as guidance for when
					to chain those actions together.
				</p>
			</div>

			<div
				className="flex items-center justify-between text-sm pt-4"
				style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
			>
				<Link
					to="/help/mcp-tools"
					className="flex items-center gap-1.5 font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "var(--km-on-surface-muted)", textDecoration: "none" }}
					onMouseEnter={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
						(e.currentTarget as HTMLElement).style.textDecoration = "underline dotted";
					}}
					onMouseLeave={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
						(e.currentTarget as HTMLElement).style.textDecoration = "none";
					}}
					onFocus={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)";
						(e.currentTarget as HTMLElement).style.textDecoration = "underline dotted";
					}}
					onBlur={(e) => {
						(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
						(e.currentTarget as HTMLElement).style.textDecoration = "none";
					}}
				>
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_back</span>
					MCP tools reference
				</Link>
				<Link
					to="/help/quick-start"
					className="flex items-center gap-1.5 transition-colors font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "#6B3FF2", textDecoration: "none" }}
					onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
					onFocus={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onBlur={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
				>
					Quick start
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_forward</span>
				</Link>
			</div>
		</article>
	);
}
